import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Badge, Button, Dialog, Field, Input, Select, Switch, Textarea } from "@/core/ui";
import { ApiError } from "@/core/api/client";
import {
  usePuzzleTypes,
  useImportPayload,
  type ImportOptionSpec,
  type ImportResultView,
  type Localized,
  type UnsupportedItem,
} from "./api";

/** Counts of constraint types in an imported definition, for the "recognised" summary. */
export function constraintCounts(definition: unknown): Record<string, number> {
  const counts: Record<string, number> = {};
  if (typeof definition !== "object" || definition === null) return counts;
  const record = definition as Record<string, unknown>;
  for (const key of ["constraints", "globalConstraints"]) {
    const list = record[key];
    if (!Array.isArray(list)) continue;
    for (const item of list) {
      if (typeof item !== "object" || item === null) continue;
      const type = (item as { type?: unknown }).type;
      if (typeof type === "string") counts[type] = (counts[type] ?? 0) + 1;
    }
  }
  return counts;
}

export function groupUnsupported(items: UnsupportedItem[]): Record<string, number> {
  const groups: Record<string, number> = {};
  for (const item of items) groups[item.kind] = (groups[item.kind] ?? 0) + 1;
  return groups;
}

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Which registered type reads the payload. The core knows no foreign format itself. */
  puzzleType: string;
  onUse: (definition: unknown, solution: unknown) => void;
}

/** Default values of the option fields the type declares. */
export function defaultOptions(schema: ImportOptionSpec[]): Record<string, unknown> {
  const values: Record<string, unknown> = {};
  for (const spec of schema) {
    values[spec.id] =
      spec.default ?? (spec.kind === "boolean" ? false : spec.kind === "integer" ? 0 : "");
  }
  return values;
}

export function ImportDialog({ open, onClose, puzzleType, onUse }: ImportDialogProps) {
  const { t, i18n } = useTranslation();
  const runImport = useImportPayload();
  const types = usePuzzleTypes();
  const info = (types.data ?? []).find((entry) => entry.key === puzzleType);
  const schema = useMemo(() => info?.import_options_schema ?? [], [info]);
  const [payload, setPayload] = useState("");
  const [options, setOptions] = useState<Record<string, unknown>>({});
  const [result, setResult] = useState<ImportResultView | null>(null);
  const [needsFetch, setNeedsFetch] = useState(false);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  useEffect(() => {
    setOptions(defaultOptions(schema));
  }, [schema]);

  const label = (value: Localized | undefined, fallback: string): string =>
    value?.[i18n.language] ?? value?.[i18n.language.split("-")[0]] ?? value?.en ?? fallback;

  const analyse = (withFetch: boolean) => {
    setErrorCode(null);
    setResult(null);
    runImport.mutate(
      {
        payload: payload.trim(),
        puzzle_type: puzzleType,
        options,
        fetch: withFetch || undefined,
      },
      {
        onSuccess: (data) => {
          setResult(data);
          setNeedsFetch(false);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === "fetch_required") {
            setNeedsFetch(true);
            return;
          }
          setErrorCode(error instanceof ApiError ? error.code : "unsupported_payload");
        },
      },
    );
  };

  const recognised = result ? constraintCounts(result.definition) : {};
  const unsupported = result ? groupUnsupported(result.unsupported) : {};

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t("backlog.import.title")}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            {t("backlog.cancel")}
          </Button>
          <Button
            disabled={!result}
            onClick={() => {
              if (!result) return;
              onUse(result.definition, result.solution);
              onClose();
            }}
          >
            {t("backlog.import.use")}
          </Button>
        </>
      }
    >
      <Field
        label={t("backlog.import.payload")}
        htmlFor="import-payload"
        hint={t("backlog.import.payload_hint")}
      >
        <Textarea
          id="import-payload"
          value={payload}
          onChange={(e) => setPayload(e.target.value)}
          spellCheck={false}
        />
      </Field>

      {schema.length > 0 ? (
        <fieldset className="bt-import__options">
          <legend className="bt-import__options-legend">{t("backlog.import.options")}</legend>
          {schema.map((spec) => (
            <ImportOptionField
              key={spec.id}
              spec={spec}
              value={options[spec.id]}
              label={label(spec.label, spec.id)}
              help={spec.help ? label(spec.help, "") : ""}
              choiceLabel={(choice) => label(choice.label, choice.value)}
              onChange={(next) => setOptions((current) => ({ ...current, [spec.id]: next }))}
            />
          ))}
        </fieldset>
      ) : null}

      <Button
        type="button"
        variant="secondary"
        loading={runImport.isPending}
        disabled={!payload.trim()}
        onClick={() => analyse(false)}
      >
        {t("backlog.import.analyse")}
      </Button>

      <div aria-live="polite" className="bt-import__report">
        {needsFetch ? (
          <Alert tone="info">
            <p className="bt-import__fetch-prompt">{t("backlog.import.fetch_required")}</p>
            <Button size="sm" onClick={() => analyse(true)} loading={runImport.isPending}>
              {t("backlog.import.fetch")}
            </Button>
          </Alert>
        ) : null}
        {errorCode ? (
          <Alert tone="danger">
            {t(`backlog.import.${errorCode}`, { defaultValue: t("app.error_generic") })}
          </Alert>
        ) : null}
        {result ? (
          <>
            {result.partial ? <Badge tone="warning">{t("backlog.import.partial")}</Badge> : null}
            <div>
              <h3 className="bt-import__report-title">{t("backlog.import.recognised")}</h3>
              <ul className="bt-import__tags">
                {Object.entries(recognised).map(([type, count]) => (
                  <li key={type}>
                    <Badge tone="success">
                      {count}× {type}
                    </Badge>
                  </li>
                ))}
              </ul>
            </div>
            {result.unsupported.length > 0 ? (
              <div>
                <h3 className="bt-import__report-title">{t("backlog.import.unsupported")}</h3>
                <ul className="bt-import__tags">
                  {Object.entries(unsupported).map(([kind, count]) => (
                    <li key={kind}>
                      <Badge tone="warning">
                        {count}× {kind}
                      </Badge>
                    </li>
                  ))}
                </ul>
                <p className="bt-import__nothing-lost">{t("backlog.import.nothing_lost")}</p>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </Dialog>
  );
}

/**
 * Renders one option field from the spec its puzzle type declared. The core never interprets
 * what an option means; it only knows the four field kinds.
 */
function ImportOptionField({
  spec,
  value,
  label,
  help,
  choiceLabel,
  onChange,
}: {
  spec: ImportOptionSpec;
  value: unknown;
  label: string;
  help: string;
  choiceLabel: (choice: { value: string; label?: Localized }) => string;
  onChange: (next: unknown) => void;
}) {
  const id = `import-option-${spec.id}`;
  if (spec.kind === "boolean") {
    return (
      <div className="bt-import__option">
        <Switch id={id} checked={Boolean(value)} onChange={onChange} label={label} />
        {help ? <p className="bt-import__option-help">{help}</p> : null}
      </div>
    );
  }
  if (spec.kind === "choice") {
    return (
      <Field label={label} htmlFor={id} hint={help}>
        <Select id={id} value={String(value ?? "")} onChange={(e) => onChange(e.target.value)}>
          {(spec.choices ?? []).map((choice) => (
            <option key={choice.value} value={choice.value}>
              {choiceLabel(choice)}
            </option>
          ))}
        </Select>
      </Field>
    );
  }
  return (
    <Field label={label} htmlFor={id} hint={help}>
      <Input
        id={id}
        type={spec.kind === "integer" ? "number" : "text"}
        value={String(value ?? "")}
        onChange={(e) =>
          onChange(spec.kind === "integer" ? Number(e.target.value) : e.target.value)
        }
      />
    </Field>
  );
}
